import { useEffect, useState } from "react"

import { trpc } from "../lib/trpc"
import { cronToNaturalLanguage, getCronPresets, validateCronExpression } from "../utils/cronUtils"

interface CronSchedule {
  id: string
  scheduleId: string | null
  name: string
  cronExpression: string
  description: string | null
  destination: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface ScheduleForm {
  name: string
  cronExpression: string
  description: string
  destination: string
  isActive: boolean
}

/**
 * CronScreen - Cron Schedule Management
 *
 * Features:
 * - View all cron schedules with natural language descriptions
 * - Create new schedules with validation
 * - Edit existing schedules
 * - Toggle active/inactive status
 * - Delete schedules
 * - Preset cron expressions
 */
function CronScreen() {
  const [schedules, setSchedules] = useState<CronSchedule[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<CronSchedule | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [showPresetsModal, setShowPresetsModal] = useState(false)

  const [form, setForm] = useState<ScheduleForm>({
    name: "",
    cronExpression: "",
    description: "",
    destination: "https://example.com/webhook",
    isActive: true,
  })

  const [validation, setValidation] = useState<{ valid: boolean; error?: string }>({ valid: true })

  // tRPC hooks
  const { data, isLoading, refetch } = trpc.qstash.listSchedules.useQuery({
    search: searchTerm || undefined,
  })

  const createMutation = trpc.qstash.createSchedule.useMutation({
    onSuccess: () => {
      void refetch()
      resetForm()
    },
  })

  const updateMutation = trpc.qstash.updateSchedule.useMutation({
    onSuccess: () => {
      void refetch()
      resetForm()
    },
  })

  const deleteMutation = trpc.qstash.deleteSchedule.useMutation({
    onSuccess: () => {
      void refetch()
    },
  })

  const toggleMutation = trpc.qstash.toggleSchedule.useMutation({
    onSuccess: () => {
      void refetch()
    },
  })

  // Update schedules when data changes
  useEffect(() => {
    if (data?.success && "schedules" in data && data.schedules) {
      setSchedules(data?.schedules as unknown as CronSchedule[])
    }
  }, [data])

  // Validate cron expression when it changes
  useEffect(() => {
    if (form.cronExpression) {
      setValidation(validateCronExpression(form.cronExpression))
    } else {
      setValidation({ valid: true })
    }
  }, [form.cronExpression])

  const resetForm = () => {
    setForm({
      name: "",
      cronExpression: "",
      description: "",
      destination: "https://example.com/webhook",
      isActive: true,
    })
    setEditingSchedule(null)
    setShowForm(false)
    setValidation({ valid: true })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validation.valid) {
      return
    }

    try {
      if (editingSchedule) {
        await updateMutation.mutateAsync({
          id: editingSchedule.id,
          ...form,
        })
      } else {
        await createMutation.mutateAsync(form)
      }
    } catch (error) {
      console.error("Failed to save schedule:", error)
    }
  }

  const handleEdit = (schedule: CronSchedule) => {
    setForm({
      name: schedule.name,
      cronExpression: schedule.cronExpression,
      description: schedule.description || "",
      destination: schedule.destination,
      isActive: schedule.isActive,
    })
    setEditingSchedule(schedule)
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this schedule?")) {
      await deleteMutation.mutateAsync({ id })
    }
  }

  const handleToggle = async (id: string) => {
    await toggleMutation.mutateAsync({ id })
  }

  const presets = getCronPresets()

  const handlePresetSelect = (cronExpression: string) => {
    setForm((prev) => ({ ...prev, cronExpression }))
    setShowPresetsModal(false)
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <header className="mb-8">
        <h1 className="text-4xl font-bold  mb-2">⏰ Cron Schedule Manager</h1>
        <p className=" text-lg">Manage scheduled tasks with QStash integration and natural language descriptions</p>
      </header>

      {/* Actions Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex gap-3">
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                     focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                     transition-colors duration-200"
          >
            ➕ New Schedule
          </button>

          <button
            onClick={() => refetch()}
            className="px-4 py-2 border border-gray-300 text-primary rounded-md
                     focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                     transition-colors duration-200"
          >
            🔄 Refresh
          </button>
        </div>

        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search schedules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Schedule Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">{editingSchedule ? "Edit Schedule" : "Create New Schedule"}</h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-primary text-2xl">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="scheduleName" className="block text-sm font-medium text-primary mb-2">
                  Schedule Name *
                </label>
                <input
                  type="text"
                  id="scheduleName"
                  required
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Daily backup task"
                />
              </div>

              <div>
                <label htmlFor="cronExpression" className="block text-sm font-medium text-primary mb-2">
                  Cron Expression *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="cronExpression"
                    required
                    value={form.cronExpression}
                    onChange={(e) => setForm((prev) => ({ ...prev, cronExpression: e.target.value }))}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-hidden
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent
                             ${validation.valid ? "border-gray-300" : "border-red-300"}`}
                    placeholder="0 0 * * *"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPresetsModal(true)}
                    className="px-3 py-2 border border-gray-300 text-primary rounded-md
                              focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  >
                    📋 Presets
                  </button>
                </div>

                {form.cronExpression && (
                  <div className="mt-2">
                    <p className={`text-sm ${validation.valid ? "text-green-600" : "text-destructive"}`}>
                      {validation.valid ? `✅ ${cronToNaturalLanguage(form.cronExpression)}` : `❌ ${validation.error}`}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="destination" className="block text-sm font-medium text-primary mb-2">
                  Destination URL *
                </label>
                <input
                  type="url"
                  id="destination"
                  required
                  value={form.destination}
                  onChange={(e) => setForm((prev) => ({ ...prev, destination: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://your-app.com/webhook"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-primary mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional description of what this schedule does..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-primary">
                  Active (schedule will run)
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={!validation.valid || createMutation.isLoading || updateMutation.isLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                           transition-colors duration-200"
                >
                  {editingSchedule ? "Update Schedule" : "Create Schedule"}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border border-gray-300 text-primary rounded-md
                           focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                           transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Presets Modal */}
      {showPresetsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">Cron Presets</h2>
              <button onClick={() => setShowPresetsModal(false)} className="text-gray-500 hover:text-primary text-2xl">
                ✕
              </button>
            </div>

            <div className="space-y-2">
              {presets.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => handlePresetSelect(preset.cron)}
                  className="w-full text-left p-3 border border-border rounded-md
                           focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <div className="font-medium ">{preset.name}</div>
                  <div className="text-sm text-gray-500 font-mono">{preset.cron}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schedules List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading schedules...</div>
          </div>
        ) : schedules.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-gray-500 text-lg mb-2">No schedules found</div>
            <button onClick={() => setShowForm(true)} className=" hover:text-blue-700 font-medium">
              Create your first schedule
            </button>
          </div>
        ) : (
          schedules.map((schedule) => (
            <div
              key={schedule.id}
              className={`border rounded-lg p-6 ${
                schedule.isActive ? "bg-background border-border" : "bg-foregroundx border-gray-300"
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold ">{schedule.name}</h3>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        schedule.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-destructive"
                      }`}
                    >
                      {schedule.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm ">
                    <div>
                      <span className="font-medium">Schedule:</span> {cronToNaturalLanguage(schedule.cronExpression)}
                    </div>
                    <div>
                      <span className="font-medium">Cron:</span>
                      <code className="ml-2 px-2 py-1 bg-foregroundx rounded font-mono text-xs">
                        {schedule.cronExpression}
                      </code>
                    </div>
                    <div>
                      <span className="font-medium">Destination:</span>
                      <a
                        href={schedule.destination}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2  hover:text-blue-700"
                      >
                        {schedule.destination}
                      </a>
                    </div>
                    {schedule.description && (
                      <div>
                        <span className="font-medium">Description:</span> {schedule.description}
                      </div>
                    )}
                    {schedule.scheduleId && (
                      <div>
                        <span className="font-medium">QStash ID:</span>
                        <code className="ml-2 px-2 py-1 bg-foregroundx rounded font-mono text-xs">
                          {schedule.scheduleId}
                        </code>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleToggle(schedule.id)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${
                      schedule.isActive
                        ? "bg-red-100 text-destructive hover:bg-red-200"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }`}
                  >
                    {schedule.isActive ? "⏸️ Pause" : "▶️ Activate"}
                  </button>

                  <button
                    onClick={() => handleEdit(schedule)}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200
                             transition-colors duration-200"
                  >
                    ✏️ Edit
                  </button>

                  <button
                    onClick={() => handleDelete(schedule.id)}
                    className="px-3 py-1 text-sm bg-red-100 text-destructive rounded-md hover:bg-red-200
                             transition-colors duration-200"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-500 border-t pt-3">
                Created: {new Date(schedule.createdAt).toLocaleString()} • Updated:{" "}
                {new Date(schedule.updatedAt).toLocaleString()}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Help Section */}
      <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">📚 Cron Expression Help</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            Cron format: <code className="bg-blue-100 px-2 py-1 rounded">minute hour day month weekday</code>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <p>
                <strong>Special characters:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <code>*</code> - any value
                </li>
                <li>
                  <code>?</code> - no specific value
                </li>
                <li>
                  <code>-</code> - range (e.g., 1-5)
                </li>
                <li>
                  <code>,</code> - list (e.g., 1,3,5)
                </li>
                <li>
                  <code>/</code> - step (e.g., */5)
                </li>
              </ul>
            </div>
            <div>
              <p>
                <strong>Examples:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <code>0 0 * * *</code> - Daily at midnight
                </li>
                <li>
                  <code>*/15 * * * *</code> - Every 15 minutes
                </li>
                <li>
                  <code>0 9-17 * * 1-5</code> - Hourly, 9-5, weekdays
                </li>
                <li>
                  <code>0 0 1 * *</code> - Monthly on the 1st
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CronScreen
